'use client'

import {
  deleteProfileLink,
  updateProfileLink,
} from '@/app/profile-manager/settings/connected-accounts/actions/profile-links'
import { ProfileLinkIcon } from '@/app/profile-manager/settings/connected-accounts/profile-link-icon'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { getProfileLinkService } from '@/resources/profile-link-services'
import { PencilIcon, Trash2Icon } from 'lucide-react'
import { useState } from 'react'
import toast from 'react-hot-toast'

export function ProfileLinkRow({ profileLink, onEdit, onChanged }) {
  const [isUpdating, setIsUpdating] = useState(false)
  const service = getProfileLinkService(profileLink.service)

  async function handleVisibilityChange(showOnProfile) {
    setIsUpdating(true)
    const result = await updateProfileLink(profileLink.id, {
      service: profileLink.service,
      username: profileLink.username,
      showOnProfile,
    })
    setIsUpdating(false)

    if (!result.success) {
      toast.error(result.message)
      return
    }

    toast.success(
      showOnProfile ? 'Link shown on profile.' : 'Link hidden from profile.',
    )
    onChanged()
  }

  async function handleDelete() {
    setIsUpdating(true)
    const result = await deleteProfileLink(profileLink.id)
    setIsUpdating(false)

    if (!result.success) {
      toast.error(result.message)
      return
    }

    toast.success(result.message)
    onChanged()
  }

  return (
    <li className="flex flex-col gap-4 border-b border-neutral-blue-700 py-5 last:border-b-0 sm:flex-row sm:items-center">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-sm bg-neutral-blue-800">
          <ProfileLinkIcon service={profileLink.service} />
        </div>
        <div className="min-w-0">
          <p className="text-p-2 font-semibold">{service?.nicename}</p>
          <p className="truncate text-p-1 text-neutral-blue-400">
            {profileLink.username}
          </p>
        </div>
      </div>

      <div className="flex items-center justify-between gap-3 sm:justify-end">
        <label className="flex items-center gap-2 text-p-1 text-neutral-blue-300">
          <Switch
            checked={profileLink.showOnProfile}
            onCheckedChange={handleVisibilityChange}
            disabled={isUpdating}
            aria-label={`Show ${service?.nicename} on profile`}
          />
          {profileLink.showOnProfile ? 'Visible' : 'Hidden'}
        </label>

        <Button
          type="button"
          variant="ghost"
          size="square"
          onClick={() => onEdit(profileLink)}
          disabled={isUpdating}
          aria-label={`Edit ${service?.nicename}`}
        >
          <PencilIcon className="h-4 w-4" />
        </Button>

        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="square"
              disabled={isUpdating}
              aria-label={`Delete ${service?.nicename}`}
            >
              <Trash2Icon className="h-4 w-4 text-error" />
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete {service?.nicename}?</AlertDialogTitle>
              <AlertDialogDescription>
                This removes the link from your Grid Finder profile.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction
                className="bg-error text-white hover:bg-error/80"
                onClick={handleDelete}
              >
                Delete
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </li>
  )
}
